import { useEffect, useState } from 'react'
import { useApp } from '../store/AppContext'
import { api } from '../lib/api'
import { ROLE_LABEL } from '../lib/types'

/** Shown when you're signed in but not in a workspace yet. */
export function WorkspaceGate() {
  const { auth, user, signOut, createWorkspace, joinWorkspace, acceptInvite } = useApp()
  const [mode, setMode] = useState<'create' | 'join'>('create')
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  const invites = auth?.invites ?? []

  // Access is granted by an administrator approving a request, so someone
  // with no workspace is usually waiting rather than stuck. Saying which is
  // the difference between a dead end and a status.
  const [pending, setPending] = useState<'loading' | 'waiting' | 'rejected' | 'none'>('loading')

  useEffect(() => {
    let cancelled = false
    void api
      .myAccessRequest()
      .then((req) => {
        if (cancelled) return
        if (!req) setPending('none')
        else if (req.status === 'rejected') setPending('rejected')
        else if (req.status === 'pending') setPending('waiting')
        else setPending('none')
      })
      .catch(() => {
        if (!cancelled) setPending('none')
      })
    return () => {
      cancelled = true
    }
  }, [])

  const go = async (fn: () => Promise<string | null>) => {
    if (busy) return
    setBusy(true)
    const problem = await fn()
    setBusy(false)
    if (problem) setErr(problem)
  }

  return (
    <div className="auth">
      <div className="auth-brandside">
        <div className="row" style={{ gap: 10 }}>
          <div className="brandmark brandmark--lg">C</div>
          <div style={{ fontWeight: 600, letterSpacing: '-.01em', fontSize: 16 }}>Cadence</div>
        </div>
        <div style={{ maxWidth: 420 }}>
          <div className="auth-eyebrow">Almost there</div>
          <h1>Start a workspace, or join one.</h1>
          <p>
            A workspace is your company or team. Whoever creates it owns it and can see everyone's time; everyone
            else joins with a code or an invite and tracks their own.
          </p>
        </div>
        <div style={{ fontSize: 13, color: 'var(--onDark)' }}>
          Signed in as {user?.email}{' '}
          <button
            type="button"
            onClick={() => void signOut()}
            style={{
              border: 0,
              background: 'transparent',
              color: '#E8794F',
              cursor: 'pointer',
              padding: 0,
              fontSize: 13,
            }}
          >
            Sign out
          </button>
        </div>
      </div>

      <div className="auth-formside">
        <div className="auth-form">
          {invites.length > 0 && (
            <div style={{ marginBottom: 26 }}>
              <h2 style={{ fontSize: 20 }}>You've been invited</h2>
              <p style={{ marginBottom: 14 }}>Accept to join and start tracking.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {invites.map((i) => (
                  <div
                    key={i.id}
                    className="card"
                    style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 10 }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="trunc" style={{ fontWeight: 600, fontSize: 14 }}>
                        {i.workspaceName}
                      </div>
                      <div style={{ fontSize: 12.5, color: 'var(--mut)' }}>as {ROLE_LABEL[i.role]}</div>
                    </div>
                    <button
                      type="button"
                      className="btn btn--primary"
                      disabled={busy}
                      onClick={() => void go(() => acceptInvite(i.id))}
                    >
                      Accept
                    </button>
                  </div>
                ))}
              </div>
              <div style={{ borderTop: '1px solid var(--line)', margin: '22px 0 0' }} />
            </div>
          )}

          {(pending === 'waiting' || pending === 'rejected') && (
            <div style={{ marginBottom: 26 }}>
              <h2 style={{ fontSize: 20 }}>
                {pending === 'waiting' ? 'Waiting for approval' : 'Request not approved'}
              </h2>
              <p style={{ marginBottom: 14 }}>
                {pending === 'waiting'
                  ? 'Your account is set up. An administrator needs to approve it before you can track time — you will get in as soon as they do.'
                  : 'An administrator did not approve this request. Speak to them if you think that is a mistake.'}
              </p>
            </div>
          )}

          <div
            className="seg"
            style={{ width: '100%', marginBottom: 20 }}
            hidden={pending === 'waiting' || pending === 'rejected'}
          >
            <button
              type="button"
              style={{ flex: 1 }}
              aria-pressed={mode === 'create'}
              onClick={() => {
                setMode('create')
                setErr('')
              }}
            >
              Create a workspace
            </button>
            <button
              type="button"
              style={{ flex: 1 }}
              aria-pressed={mode === 'join'}
              onClick={() => {
                setMode('join')
                setErr('')
              }}
            >
              Join with a code
            </button>
          </div>

          {mode === 'create' ? (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                void go(() => createWorkspace(name))
              }}
            >
              <label className="label" htmlFor="wg-name">
                Company or team name
              </label>
              <input
                id="wg-name"
                className="input"
                value={name}
                onChange={(e) => {
                  setName(e.target.value)
                  setErr('')
                }}
                placeholder="e.g. Fieldwork Studio"
                style={{ marginBottom: 14 }}
              />
              <p style={{ fontSize: 12.5, color: 'var(--mut)', margin: '0 0 16px' }} className="pretty">
                You'll be the owner. Afterwards you get a join code to share, and you can invite people by email.
              </p>
              {err && <div className="auth-err">{err}</div>}
              <button type="submit" className="btn btn--primary btn--block btn--lg" disabled={busy}>
                {busy ? 'Creating…' : 'Create workspace'}
              </button>
            </form>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                void go(() => joinWorkspace(code))
              }}
            >
              <label className="label" htmlFor="wg-code">
                Join code
              </label>
              <input
                id="wg-code"
                className="input mono"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.toUpperCase())
                  setErr('')
                }}
                placeholder="ABCD-2345"
                style={{ marginBottom: 14, letterSpacing: '.08em' }}
              />
              <p style={{ fontSize: 12.5, color: 'var(--mut)', margin: '0 0 16px' }} className="pretty">
                Ask the workspace owner or an admin for the code — they'll find it in Settings.
              </p>
              {err && <div className="auth-err">{err}</div>}
              <button type="submit" className="btn btn--primary btn--block btn--lg" disabled={busy}>
                {busy ? 'Joining…' : 'Join workspace'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
